import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    TextField, FormControl, InputLabel, Select, MenuItem, Grid
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ICreateNotification, INotification, NotificationSeverity } from '../../models/INotification';
import NotificationServiceInstance from '../../services/NotificationService';
import { getApiErrorDescription } from '../../services/ServiceConstants';
import DateHelperInstance from '../../services/DateHelper';

interface CreateNotificationDialogProps {
    open: boolean;
    raceId: string;
    riderId?: string;
    notification?: INotification;
    onClose: () => void;
    onCreated?: () => void;
}

function getLocalDateTimeString(date: Date): string {
    const offset = date.getTimezoneOffset();
    const local = new Date(date.getTime() - offset * 60000);
    return local.toISOString().slice(0, 16);
}

export default function CreateNotificationDialog(props: CreateNotificationDialogProps) {
    const { open, raceId, riderId, notification, onClose, onCreated } = props;
    const { t } = useTranslation('common');

    const getDefaultStartTime = () => getLocalDateTimeString(new Date());

    const [message, setMessage] = useState('');
    const [severity, setSeverity] = useState<NotificationSeverity>(NotificationSeverity.Information);
    const [startTime, setStartTime] = useState(getDefaultStartTime());
    const [endTime, setEndTime] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string>();

    const isEditing = Boolean(notification);

    useEffect(() => {
        if (open && notification) {
            setMessage(notification.description ?? '');
            setSeverity(notification.severity);
            setStartTime(getLocalDateTimeString(new Date(notification.notificationStart)));
            setEndTime(notification.notificationEnd
                ? getLocalDateTimeString(new Date(notification.notificationEnd))
                : '');
        } else if (open) {
            setMessage('');
            setSeverity(NotificationSeverity.Information);
            setStartTime(getDefaultStartTime());
            setEndTime('');
        }
    }, [open, notification]);

    const handleSave = async () => {
        if (!message.trim()) {
            setError(t('notifications.errorMessageRequired') as string);
            return;
        }

        // Validate dates
        const parsedStartTime = DateHelperInstance.parseDateTimePickString(startTime);

        if (!DateHelperInstance.isValidDate(parsedStartTime)) {
            setError(t('notifications.errorInvalidStartTime') as string);
            return;
        }

        let parsedEndTime: Date | null = null;
        if (endTime) {
            parsedEndTime = DateHelperInstance.parseDateTimePickString(endTime);
            if (!DateHelperInstance.isValidDate(parsedEndTime)) {
                setError(t('notifications.errorInvalidEndTime') as string);
                return;
            }
            if (parsedStartTime >= parsedEndTime) {
                setError(t('notifications.errorStartTimeAfterEndTime') as string);
                return;
            }
        }

        setSaving(true);
        setError(undefined);

        try {
            const payload: ICreateNotification = {
                description: message.trim(),
                severity,
                notificationStart: parsedStartTime,
                notificationEnd: parsedEndTime,
            };

            if (isEditing && notification) {
                await NotificationServiceInstance.updateEventNotification(raceId, notification.notificationId, payload);
            } else if (riderId) {
                await NotificationServiceInstance.createRiderNotification(raceId, riderId, payload);
            } else {
                await NotificationServiceInstance.createEventNotification(raceId, payload);
            }

            // Reset form
            setMessage('');
            setSeverity(NotificationSeverity.Information);
            setStartTime(getDefaultStartTime());
            setEndTime('');

            onCreated?.();
            onClose();
        } catch (e) {
            setError(getApiErrorDescription(e));
        } finally {
            setSaving(false);
        }
    };

    const handleClose = () => {
        setError(undefined);
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {isEditing
                    ? t('notifications.editNotification')
                    : riderId
                        ? t('notifications.createRiderNotification')
                        : t('notifications.createEventNotification')
                }
            </DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid item xs={12}>
                        <TextField
                            id="notification-message"
                            label={t('notifications.message')}
                            value={message}
                            onChange={e => setMessage(e.target.value)}
                            fullWidth
                            multiline
                            minRows={2}
                            required
                            error={Boolean(error)}
                            helperText={error}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <InputLabel id="notification-severity-label">{t('notifications.severity')}</InputLabel>
                            <Select
                                labelId="notification-severity-label"
                                id="notification-severity"
                                value={severity}
                                label={t('notifications.severity')}
                                onChange={e => setSeverity(e.target.value as NotificationSeverity)}
                            >
                                <MenuItem value={NotificationSeverity.Information}>{t('notifications.severityInfo')}</MenuItem>
                                <MenuItem value={NotificationSeverity.Important}>{t('notifications.severityImportant')}</MenuItem>
                                <MenuItem value={NotificationSeverity.Danger}>{t('notifications.severityDanger')}</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            id="notification-start-time"
                            label={t('notifications.startTime')}
                            type="datetime-local"
                            value={startTime}
                            onChange={e => setStartTime(e.target.value)}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            id="notification-end-time"
                            label={t('notifications.endTime')}
                            type="datetime-local"
                            value={endTime}
                            onChange={e => setEndTime(e.target.value)}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={saving}>
                    {t('commonActions.cancel')}
                </Button>
                <Button onClick={handleSave} variant="contained" color="primary" disabled={saving}>
                    {t('commonActions.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
